/**
 * /academy/attempt/:id/results — presented like the certificate (score /699 or /20, CEFR, NCLC),
 * always as an estimate; then the review: questions with the right answer and explanation,
 * productions with their grade (criteria, errors, corrected version, advice).
 */

import {
  ApiError,
  useAcademyAttemptsRetrieve,
  useAcademyAttemptsReviewRetrieve,
  useAcademyFormatRetrieve,
} from "@agencyos/api-client";
import { hasPictures } from "@agencyos/core";
import { Badge, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { ArrowLeft, CheckCircle2, Hourglass, Lightbulb, Sprout, XCircle } from "lucide-react";
import { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { MarkNotes } from "./MarkNotes";

import { BeginnerNext } from "./AgencyName";
import { FalseFriendsList } from "./FalseFriendsList";
import { RecordingExpired } from "./RecordingExpired";
import { ExamPictureView, PictureOptions } from "./ExamPicture";
import { AudioReplay, ListeningReplay } from "./ListeningReplay";
import {
  type ReviewProduction,
  type RubricRow,
  SKILLS,
  type Results,
  isObject,
  isSkill,
  localized,
  parseResults,
  parseReview,
  parseRubrics,
  scaleMax,
} from "./types";
import { CardTitle, PageHead, Ring, SectionTitle, SkillIcon } from "./ui";
import { AiUnavailable } from "./AiUnavailable";
import { unavailableSkills, useAcademyPaths } from "./paths";
import { MethodLink } from "./ielts/MethodLink";
import { FeedbackInMyLanguage, InMyLanguage } from "./ielts/InMyLanguage";

function waitingFor(results: Results): "all" | "some" | "none" {
  const shown = SKILLS.filter((s) => results[s]);
  if (!shown.length && !results.beginner) return "all";
  return shown.some((s) => results[s]?.pending) ? "some" : "none";
}

function ScoreCards({ results, sections, unavailable }: { results: Results; sections: unknown; unavailable?: Set<string> }) {
  const { t } = useTranslation();
  const shown = SKILLS.filter((s) => results[s]);
  return (
    <ul className="ac-scores">
      {shown.map((skill, index) => {
        const row = results[skill];
        const max = scaleMax(skill, sections);
        const min = skill === "co" || skill === "ce" ? 100 : 0;
        return (
          <li key={skill} className={`ac-score ac-skill--${skill} ao-enter`} style={{ "--ao-i": index } as CSSProperties}>
            <div className="ac-score__head">
              <SkillIcon skill={skill} size={16} />
              <h2 className="ac-headline">{t(`academy.skill.${skill}`)}</h2>
            </div>
            {unavailable?.has(skill) ? (
              <AiUnavailable />
            ) : row?.pending || row?.score === undefined ? (
              <p className="ac-status ac-secondary ac-score__pending">
                <Hourglass aria-hidden size={18} />
                {t("academy.results.grading")}
              </p>
            ) : (
              <>
                <div
                  role="meter"
                  aria-label={max ? t("academy.results.scoreOf", { score: row.score, max }) : String(row.score)}
                  aria-valuemin={min}
                  aria-valuemax={max ?? row.score}
                  aria-valuenow={row.score}
                >
                  <Ring ratio={max ? (row.score - min) / (max - min) : 1} size={132} stroke={11} tone={skill}>
                    <span className="ac-score__value ao-num">{row.score}</span>
                    {max ? <span className="ac-score__max ao-num">/ {max}</span> : null}
                  </Ring>
                </div>
                <p className="ac-score__levels">
                  <span className="ac-score__nclc">{t("academy.results.nclc", { n: row.nclc ?? 0 })}</span>
                  <Badge>{t("academy.results.cefr", { level: row.cefr ?? "" })}</Badge>
                </p>
                {row.questions !== undefined && (
                  <p className="ac-caption">{t("academy.results.right", { right: row.right ?? 0, total: row.questions })}</p>
                )}
                {(row.unanswered ?? 0) > 0 && (
                  <p className="ac-caption ao-num ko">{t("academy.results.unansweredCount", { count: row.unanswered, total: row.tasksPlanned })}</p>
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function ProductionReview({ production, rubric }: { production: ReviewProduction; rubric: RubricRow[] }) {
  const { t, i18n } = useTranslation();
  const { grade } = production;
  const skill = isSkill(production.skill) ? production.skill : "ee";
  const labelOf = (key: string) => {
    const row = rubric.find((r) => r.key === key);
    return { label: localized(row?.label, i18n.language) || key, max: row?.max };
  };
  return (
    <article className="ac-production">
      <h3 className="ac-title-3">
        {t(`academy.skill.${skill}`)} · {t("academy.writing.task", { n: production.task })}
        {production.score !== null && ` · ${t("academy.results.onTwenty", { score: production.score })}`}
      </h3>
      <p className="ac-prompt ac-prompt--small" lang="fr">
        {production.prompt}
      </p>
      {production.text && (
        <details>
          <summary>{skill === "eo" ? t("academy.results.transcript") : t("academy.results.yourText")}</summary>
          <p className="ac-production-text" lang="fr">
            {production.text}
          </p>
          {skill === "ee" && <p className="ac-footnote">{t("academy.writing.wordsPlain", { count: production.wordCount })}</p>}
        </details>
      )}
      {skill === "eo" && production.audioUrl && (
        <div className="ac-listen-again">
          <p className="ac-label">{t("academy.results.listenAgain")}</p>
          {/* The learner's own recording, as often as wanted (D-106); its transcript is above. */}
          <AudioReplay url={production.audioUrl} label={t("academy.results.listenAgain")} />
        </div>
      )}
      {production.audioExpired && <RecordingExpired />}
      {production.unanswered && <p className="ac-status ko">{t("academy.results.unanswered")}</p>}
      <MarkNotes grade={production.rawGrade} productionId={production.id} />
      {production.status === "to_trainer" && (
        <p className="ac-status ac-secondary">
          <Hourglass aria-hidden size={18} />
          {t("academy.results.toTrainer")}
        </p>
      )}
      {production.status === "submitted" && (
        <p className="ac-status ac-secondary">
          <Hourglass aria-hidden size={18} />
          {t("academy.results.grading")}
        </p>
      )}
      {grade.warning && <p className="ac-note ac-note--warning">{grade.warning}</p>}
      {grade.criteria.length > 0 && (
        <table className="ac-criteria">
          <caption className="visually-hidden">{t("academy.results.criteria")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("academy.results.criterion")}</th>
              <th scope="col" className="num">
                {t("academy.results.points")}
              </th>
            </tr>
          </thead>
          <tbody>
            {grade.criteria.map((c) => {
              const { label, max } = labelOf(c.key);
              return (
                <tr key={c.key}>
                  <td>
                    <strong>{label}</strong>
                    {c.comment && <p className="ac-footnote">{c.comment}</p>}
                    {max ? (
                      <span className="ac-bar" aria-hidden="true">
                        <span style={{ transform: `scaleX(${Math.min(1, c.points / max)})` }} />
                      </span>
                    ) : null}
                  </td>
                  <td className="num ao-num">{max !== undefined ? `${c.points} / ${max}` : c.points}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {grade.comment && <p className="ac-comment">{grade.comment}</p>}
      <FeedbackInMyLanguage comment={grade.comment} errors={grade.errors} examLanguage="fr" />
      {grade.errors.length > 0 && (
        <div className="ac-block">
          <h4 className="ac-label">{t("academy.results.errors")}</h4>
          <ul className="ac-errors">
            {grade.errors.map((e, index) => (
              <li key={index} className="ac-error-row academy-error-row">
                <p lang="fr">
                  <del>{e.excerpt}</del> → <ins>{e.correction}</ins>
                </p>
                {e.explanation && <p className="ac-footnote">{e.explanation}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <FalseFriendsList hits={grade.falseFriends} />
      {grade.corrected && (
        <details>
          <summary>{t("academy.results.corrected")}</summary>
          <p className="ac-production-text" lang="fr">
            {grade.corrected}
          </p>
        </details>
      )}
      {grade.model && (
        <details>
          <summary>{t("academy.results.model")}</summary>
          <p className="ac-production-text" lang="fr">
            {grade.model}
          </p>
        </details>
      )}
      {production.score !== null && !grade.model && (
        <p className="ac-footnote ac-review__why--pending">{t("listening.review.modelSoon")}</p>
      )}
      {grade.advice.length > 0 && (
        <div className="ac-block ac-advice">
          <h4 className="ac-label">
            <Lightbulb aria-hidden size={14} /> {t("academy.results.advice")}
          </h4>
          <ul>
            {grade.advice.map((a, index) => (
              <li key={index}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </article>
  );
}

export function ResultsPage() {
  const paths = useAcademyPaths();
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const attempt = useAcademyAttemptsRetrieve(id, { query: { retry: false } });
  const attemptState = attempt.data?.status === 200 ? attempt.data.data : undefined;
  const inProgress = attemptState?.status === "in_progress";
  const review = useAcademyAttemptsReviewRetrieve(id, {
    query: {
      enabled: Boolean(attemptState) && !inProgress,
      retry: false,
      refetchInterval: (query) => {
        const data = query.state.data?.status === 200 ? parseReview(query.state.data.data) : null;
        const waiting = data ? waitingFor(data.results) : "all";
        return waiting === "all" ? 4000 : waiting === "some" ? 20000 : false;
      },
    },
  });
  const format = useAcademyFormatRetrieve(undefined, { query: { staleTime: 3_600_000 } });
  const formatData = format.data?.status === 200 ? format.data.data : undefined;
  const rubrics = useMemo(() => parseRubrics(formatData?.rubrics), [formatData]);
  const data = review.data?.status === 200 ? parseReview(review.data.data) : null;
  const stateResults = isObject(attemptState) ? parseResults((attemptState as Record<string, unknown>).results) : {};
  const beginner = attemptState?.status === "stopped" || stateResults.beginner === true || data?.results.beginner === true;

  const back = (
    <Link to={paths.home} className="ac-back">
      <ArrowLeft aria-hidden size={16} />
      {t("academy.results.back")}
    </Link>
  );

  if (attempt.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (attempt.error instanceof ApiError && attempt.error.status === 404) {
    return (
      <div className="ac-page">
        {back}
        <p className="ac-secondary">{t("academy.results.notFound")}</p>
      </div>
    );
  }
  if (inProgress) {
    return (
      <div className="ac-surface ac-card ac-card--center">
        <p>{t("academy.results.notFinished")}</p>
        <Link className={buttonClass()} to={paths.attempt(id)}>
          {t("academy.history.resume")}
        </Link>
      </div>
    );
  }

  if (beginner) {
    return (
      <div className="ac-page">
        {back}
        <GlassCard className="ac-card ac-card--success">
          <CardTitle as="h1" icon={<Sprout size={16} />} tone="success">
            {t("academy.beginner.title")}
          </CardTitle>
          <p>{t("academy.beginner.text")}</p>
          <BeginnerNext />
          <Link className={buttonClass("primary")} to="/academy/course">
            {t("academyCourse.home.startFromZero")}
          </Link>
        </GlassCard>
      </div>
    );
  }

  const results = data?.results ?? {};
  const waiting = waitingFor(results);
  const questions = data?.questions ?? [];
  const productions = data?.productions ?? [];

  return (
    <div className="ac-page ac-results">
      {back}
      <PageHead
        eyebrow={t("academy.ui.results.eyebrow")}
        title={t("academy.results.title")}
        subtitle={
          <p className="ac-note" role="note">
            {t("academy.results.estimateLong")}
          </p>
        }
      />

      {(review.isLoading || waiting === "all") && (
        <div className="ac-surface ac-card ac-card--center ac-preparing">
          <span className="ac-wave ac-wave--small" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((n) => (
              <span key={n} style={{ animationDelay: `${n * -0.15}s` }} />
            ))}
          </span>
          <p className="ac-status" role="status">
            {t("academy.results.preparing")}
          </p>
        </div>
      )}

      {waiting !== "all" && <ScoreCards results={results} sections={formatData?.sections} unavailable={unavailableSkills(attemptState)} />}

      {productions.length > 0 && (
        <section className="ac-section" aria-labelledby="academy-productions">
          <SectionTitle id="academy-productions">{t("academy.results.productions")}</SectionTitle>
          <div className="ac-stack-cards">
            {productions.map((p) => (
              <GlassCard key={`${p.skill}-${p.task}`} as="div" className="ac-card">
                <ProductionReview production={p} rubric={rubrics[p.skill] ?? []} />
              </GlassCard>
            ))}
          </div>
        </section>
      )}

      {questions.length > 0 && (
        <section className="ac-section" aria-labelledby="academy-questions">
          <SectionTitle id="academy-questions">{t("academy.results.questions")}</SectionTitle>
          <div className="ac-method-links">
            {[...new Set(questions.map((q) => q.skill))].map((skill) => (
              <MethodLink key={skill} exam="tcf_canada" skill={skill} />
            ))}
          </div>
          <ol className="ac-review-list">
            {questions.map((q, index) => (
              <li key={index} className={`ac-review ac-surface${q.correct ? " ac-review--right" : " ac-review--wrong"}`}>
                <p className="ac-review__head">
                  <span className="ac-review__mark" aria-hidden="true">
                    {q.correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                  </span>
                  <strong className="ac-caption">
                    {isSkill(q.skill) ? t(`academy.skillShort.${q.skill}`) : q.skill} · {q.level}
                  </strong>
                  <span className="visually-hidden">{q.correct ? t("academy.player.right") : t("academy.results.wrongShort")}</span>
                </p>
                {q.skill === "co" && (q.audioUrl || q.transcript.length > 0) ? (
                  <ListeningReplay audioUrl={q.audioUrl} lines={q.transcript} />
                ) : (
                  q.document && (
                    <details>
                      <summary>{q.skill === "co" ? t("academy.results.script") : t("academy.results.document")}</summary>
                      <p className="ac-document ac-document--small" lang="fr">
                        {q.document}
                      </p>
                    </details>
                  )
                )}
                <ExamPictureView picture={q.image} lang="fr" size="medium" />
                <p className="ac-review__stem" lang="fr">
                  {q.stem}
                </p>
                {hasPictures(q.optionImages) ? (
                  <PictureOptions
                    name={`r-${index}`}
                    options={q.options}
                    images={q.optionImages}
                    answer={q.answer}
                    given={q.choice}
                    showText
                    lang="fr"
                  />
                ) : (
                <ul className="ac-review__options" lang="fr">
                  {q.options.map((option, i) => (
                    <li
                      key={i}
                      className={`ac-review__option${i === q.answer ? " ac-review__option--right" : ""}${
                        i === q.choice && i !== q.answer ? " ac-review__option--wrong" : ""
                      }`}
                    >
                      {option}
                      {i === q.answer && <span className="visually-hidden"> {t("academy.results.rightAnswer")}</span>}
                      {i === q.choice && <span className="ac-review__yours"> · {t("academy.results.yourAnswer")}</span>}
                    </li>
                  ))}
                </ul>
                )}
                {q.choice < 0 && <p className="ac-footnote">{t("academy.results.noAnswer")}</p>}
                {q.explanation ? (
                  <>
                    <p className="ac-review__why">{q.explanation}</p>
                    <InMyLanguage text={q.explanation} source="fr" examLanguage="fr" kind="correction" />
                  </>
                ) : (
                  <p className="ac-review__why ac-review__why--pending">{t("listening.review.explanationSoon")}</p>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}
